<template>
  <div :style="newStyle" v-bind="$attrs" :class="newClass">
    <template v-if="props.themeType === 'titleTag'">
      <Svg class="theme-tag-titleTagBg"></Svg>
      <div class="theme-tag-titleTagText"><slot></slot></div>
    </template>
    <template v-else>
      <slot></slot>
    </template>
  </div>
</template>

<script setup lang="ts">
  import Svg from './component/svg.vue';
  import { ref, watchEffect } from 'vue';
  import { useTheme } from '@shared/components/theme-components/use-theme';
  import { hex2RgbColor } from '@shared/components/theme-components/utils';

  interface Props {
    bgColor?: string;
    txtColor?: string;
    borderColor?: string;
    themeType?: 'titleTag' | 'solidTag' | 'outLineTag'; // 带svg背景的标题标签，实心标签，朴素标签
    size?: 'normal' | 'middle' | 'large';
    round?: boolean;
    opacity?: number;
  }

  const props = withDefaults(defineProps<Props>(), {
    bgColor: '',
    txtColor: '',
    borderColor: '',
    themeType: 'outLineTag',
    size: 'normal',
    round: false,
    opacity: 1,
  });

  const newStyle = ref({});
  const handlerStyle = () => {
    const newthemeStyle = useTheme('tag');

    // 处理默认色反转实心颜色
    if (props.themeType === 'solidTag') {
      newthemeStyle['--backgroundColor'] = newthemeStyle['--textColor'];
    }

    //  背景色和字体色支持单独设置(优先级更高)
    if (props.bgColor) {
      //更新背景色
      newthemeStyle['--backgroundColor'] = props.bgColor;
      newthemeStyle['--borderColor'] = props.borderColor ?? props.bgColor;
    }
    if (props.txtColor) {
      //更新文字色
      newthemeStyle['--textColor'] = props.txtColor;
    }
    if (props.borderColor) {
      //更新边框
      newthemeStyle['--borderColor'] = props.borderColor;
    }

    //  降低背景色透明度
    newthemeStyle['--backgroundColor'] = hex2RgbColor(newthemeStyle['--backgroundColor'], props.opacity);

    newStyle.value = { ...newthemeStyle };
  };

  const newClass = ref<any>([]);
  const handlerClass = () => {
    //处理是否朴素样式  实心   svg的titletag
    const type =
      props.themeType === 'solidTag'
        ? 'theme-tag-solid'
        : props.themeType === 'outLineTag'
        ? 'theme-tag-outline'
        : 'theme-tag-titleTag';

    // 处理size
    const size = props.size === 'middle' ? 'theme-tag-middle' : props.size === 'large' ? 'theme-tag-large' : '';

    //处理圆角
    const rounds = props.round ? 'theme-tags-round' : '';

    newClass.value = [type, size, rounds];
  };

  watchEffect(() => {
    handlerClass();
    handlerStyle();
  });
</script>

<style scoped lang="less">
  .theme-tag-outline {
    display: inline-flex;
    align-items: center;
    padding: 0 5px;
    font-size: 12px;
    font-weight: 400;
    color: var(--textColor);
    vertical-align: middle;
    background: #fff;
    border: 1px solid var(--borderColor);
    border-radius: 2px;
  }

  .theme-tag-solid {
    .theme-tag-outline;

    position: relative;
    font-size: 11px;
    background-color: var(--backgroundColor);
  }

  .theme-tags-round {
    border-radius: 999px;
  }

  .theme-tag-middle {
    padding: 2px 6px;
  }

  .theme-tag-large {
    padding: 4px 8px;
    font-size: 14px;
    border-radius: 4px;
  }

  .theme-tag-titleTag {
    position: relative;
    display: inline-block;
    min-width: 88px;
    height: 18px;
    overflow: hidden;
    border-radius: 0 6px 0 0;
  }

  .theme-tag-titleTagBg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .theme-tag-titleTagText {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    line-height: 15px;
    line-height: 1;
    padding: 0 8px 0 18px;
    font-size: 11px;
    font-weight: 600;
    color: var(--textColor);
  }
</style>
